import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Users,
  Flame,
  CheckCircle2,
  Loader2,
  Share2,
  CalendarClock,
  MapPin,
  Megaphone,
  Volume2,
  VolumeX,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../lib/api";
import useUTM, { utmPayload } from "../lib/useUTM";

/**
 * Rally — public launch rally sign-up page.
 *
 * Design pillars:
 *   - Hero video (silent-autoplay by default; one-tap unmute) playing the
 *     60-second Rise II Power hype cut set to Sekhem Re.
 *   - Rally flyer rendered on top for immediate credibility.
 *   - RSVP form (name/email/phone/city/state/zip/interests/reminders) that
 *     posts to /api/rally/signup, dedupes by email, and returns the user's
 *     position (#N on the roll).
 *   - Live supporter ticker + state heatmap under the form.
 *   - Share buttons to spread the message virally.
 *
 * No "fake" date. We honour the "TBA" brief by locking the "YOU'LL BE THE
 * FIRST TO KNOW" promise + reminders opt-in instead of inventing a date.
 */

const INTEREST_OPTIONS = [
  { key: "attend", label: "Attend in person", icon: "🙌" },
  { key: "livestream", label: "Join the livestream", icon: "📺" },
  { key: "speaker", label: "Speak at the rally", icon: "🎤" },
  { key: "volunteer", label: "Volunteer / organize", icon: "🤝" },
  { key: "donate", label: "Sponsor the rally", icon: "💵" },
  { key: "performer", label: "Perform (artist / spoken word)", icon: "🎶" },
  { key: "vendor", label: "Vendor booth", icon: "🛍️" },
  { key: "press", label: "Press / media", icon: "📰" },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR",
];

const RALLY_VIDEO_VERTICAL = `${API}/static/marketing/rally_of_the_minds_vertical.mp4?v=1`;
const RALLY_VIDEO_HORIZONTAL = `${API}/static/marketing/rally_of_the_minds_horizontal.mp4?v=1`;

function LiveTicker({ recent }) {
  if (!recent || recent.length === 0) return null;
  return (
    <div
      className="overflow-hidden border-y border-bdu-gold/10 bg-black/40 py-2.5"
      data-testid="rally-ticker"
    >
      <div className="flex gap-10 animate-[ticker_30s_linear_infinite] whitespace-nowrap">
        {[...recent, ...recent, ...recent].map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs uppercase tracking-widest text-bdu-cream/70">
            <Flame size={12} className="text-bdu-gold" />
            <span className="font-semibold text-bdu-cream">{r.first_name}</span>
            {r.location && (
              <>
                <span className="text-bdu-cream/40">from</span>
                <span>{r.location}</span>
              </>
            )}
            <span className="text-bdu-cream/40">just RSVP'd</span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-33.333%); }
        }
      `}</style>
    </div>
  );
}

function HeroVideo() {
  const [isVertical, setIsVertical] = useState(true);
  const [muted, setMuted] = useState(true);
  const vidRef = useRef(null);

  const toggleMute = () => {
    const el = vidRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
    if (!el.muted) {
      el.play().catch(() => {});
    }
  };

  return (
    <div
      className="relative"
      data-testid="rally-hero-video-wrapper"
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="label-eyebrow" style={{ fontSize: "0.55rem" }}>
          The 60-second rally cut · Sekhem Re
        </div>
        <div
          className="inline-flex items-center gap-1 bg-bdu-surface2 border border-bdu-gold/20 rounded-full p-0.5"
          data-testid="rally-video-toggle"
        >
          <button
            type="button"
            onClick={() => setIsVertical(false)}
            className={`px-3 py-1 text-[0.65rem] uppercase tracking-widest transition rounded-full ${
              !isVertical
                ? "bg-bdu-gold text-black font-semibold"
                : "text-bdu-cream/60 hover:text-bdu-cream"
            }`}
            data-testid="rally-video-toggle-horizontal"
          >
            Landscape
          </button>
          <button
            type="button"
            onClick={() => setIsVertical(true)}
            className={`px-3 py-1 text-[0.65rem] uppercase tracking-widest transition rounded-full ${
              isVertical
                ? "bg-bdu-gold text-black font-semibold"
                : "text-bdu-cream/60 hover:text-bdu-cream"
            }`}
            data-testid="rally-video-toggle-vertical"
          >
            Reel
          </button>
        </div>
      </div>

      <div
        className={`relative rounded-lg overflow-hidden ring-1 ring-bdu-gold/30 shadow-[0_30px_80px_-10px_rgba(212,175,55,0.35)] bg-black ${
          isVertical ? "aspect-[9/16] max-w-sm mx-auto" : "aspect-video"
        }`}
      >
        <video
          ref={vidRef}
          key={isVertical ? "v" : "h"}
          src={isVertical ? RALLY_VIDEO_VERTICAL : RALLY_VIDEO_HORIZONTAL}
          className="w-full h-full object-contain bg-black"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controls={false}
          data-testid="rally-hero-video"
        />
        <button
          type="button"
          onClick={toggleMute}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-2 bg-black/70 border border-bdu-gold/40 text-bdu-cream text-xs uppercase tracking-widest hover:bg-bdu-gold hover:text-black transition"
          data-testid="rally-video-mute-toggle"
        >
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          {muted ? "Unmute" : "Mute"}
        </button>
      </div>
    </div>
  );
}

function RSVPForm({ onSuccess }) {
  const utm = useUTM();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    zip: "",
    reminders: true,
  });
  const [picked, setPicked] = useState(new Set(["attend"]));
  const [submitting, setSubmitting] = useState(false);

  const togglePick = (k) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/rally/signup", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        city: form.city.trim() || null,
        state: form.state || null,
        zip: form.zip.trim() || null,
        interests: Array.from(picked),
        reminders: form.reminders,
        referrer: typeof window !== "undefined" ? document.referrer : null,
        ...utmPayload(utm),
      });
      toast.success(`You're #${r.data.position} on the rally roll!`);
      onSuccess(r.data);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Signup failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-bdu-surface2 border border-bdu-gold/25 p-6 sm:p-8 shadow-[0_30px_80px_-20px_rgba(212,175,55,0.3)]"
      data-testid="rally-rsvp-form"
    >
      <div className="label-eyebrow">RSVP · Reserve Your Seat</div>
      <h3 className="mt-3 font-serif text-2xl sm:text-3xl text-bdu-cream">
        Add your name to the <span className="text-bdu-gold italic">rally roll</span>.
      </h3>
      <p className="mt-2 text-sm text-bdu-cream/60 leading-relaxed">
        The date and venue drop the second we hit 500 RSVPs. You'll be the first to know.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label-eyebrow block mb-2" htmlFor="rally-name">
            Full name <span className="text-red-300">*</span>
          </label>
          <input
            id="rally-name"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-black/40 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
            placeholder="Marcus Johnson"
            data-testid="rally-field-name"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2" htmlFor="rally-email">
            Email <span className="text-red-300">*</span>
          </label>
          <input
            id="rally-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-black/40 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
            placeholder="marcus@example.com"
            data-testid="rally-field-email"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-2" htmlFor="rally-phone">
            Phone (for SMS reminders)
          </label>
          <input
            id="rally-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-black/40 border border-bdu-gold/20 px-4 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
            placeholder="404-555-0100"
            data-testid="rally-field-phone"
          />
        </div>
        <div className="grid grid-cols-6 gap-2">
          <div className="col-span-3">
            <label className="label-eyebrow block mb-2" htmlFor="rally-city">
              City
            </label>
            <input
              id="rally-city"
              type="text"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="w-full bg-black/40 border border-bdu-gold/20 px-3 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
              placeholder="Atlanta"
              data-testid="rally-field-city"
            />
          </div>
          <div className="col-span-1">
            <label className="label-eyebrow block mb-2" htmlFor="rally-state">
              State
            </label>
            <select
              id="rally-state"
              value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })}
              className="w-full bg-black/40 border border-bdu-gold/20 px-2 py-3 text-bdu-cream focus:outline-none focus:border-bdu-gold transition text-sm"
              data-testid="rally-field-state"
            >
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-2" htmlFor="rally-zip">
              ZIP
            </label>
            <input
              id="rally-zip"
              type="text"
              inputMode="numeric"
              pattern="\d{5}"
              maxLength={5}
              value={form.zip}
              onChange={(e) => setForm({ ...form, zip: e.target.value.replace(/\D/g, "") })}
              className="w-full bg-black/40 border border-bdu-gold/20 px-3 py-3 text-bdu-cream placeholder:text-bdu-cream/30 focus:outline-none focus:border-bdu-gold transition text-sm"
              placeholder="30309"
              data-testid="rally-field-zip"
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="label-eyebrow mb-3">How do you want to show up?</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {INTEREST_OPTIONS.map((opt) => {
            const active = picked.has(opt.key);
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => togglePick(opt.key)}
                data-testid={`rally-interest-${opt.key}`}
                className={`text-left px-3 py-2.5 border transition flex items-center gap-2 text-xs ${
                  active
                    ? "bg-bdu-gold/15 border-bdu-gold text-bdu-cream font-semibold"
                    : "bg-black/30 border-bdu-cream/10 text-bdu-cream/70 hover:border-bdu-cream/30"
                }`}
              >
                <span className="text-base">{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer text-xs text-bdu-cream/70 leading-relaxed">
        <input
          type="checkbox"
          checked={form.reminders}
          onChange={(e) => setForm({ ...form, reminders: e.target.checked })}
          className="mt-0.5 accent-bdu-gold"
          data-testid="rally-field-reminders"
        />
        <span>
          Send me the rally date, venue, and livestream link the second it's locked in — plus a 24-hour and 1-hour reminder.
        </span>
      </label>

      <button
        type="submit"
        disabled={submitting}
        className="btn-gold w-full mt-7 justify-center"
        data-testid="rally-submit-btn"
      >
        {submitting ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Reserving your seat…
          </>
        ) : (
          <>
            Reserve My Seat <ArrowRight size={16} />
          </>
        )}
      </button>

      <p className="mt-4 text-[0.65rem] uppercase tracking-widest text-bdu-cream/40 text-center">
        No spam. One list. Protected by BDU · Powered by the Movement.
      </p>
    </form>
  );
}

function ConfirmationCard({ result, onShare, onClose }) {
  if (!result) return null;
  const { position, total, goal, progress_pct, signup } = result;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="rally-confirmation-modal"
    >
      <div
        className="relative bg-gradient-to-br from-bdu-surface2 to-black border border-bdu-gold max-w-lg w-full p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-bdu-cream/50 hover:text-bdu-cream text-xl leading-none"
          aria-label="Close"
        >
          ×
        </button>
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-bdu-gold/20 border border-bdu-gold flex items-center justify-center">
            <CheckCircle2 size={28} className="text-bdu-gold" />
          </div>
        </div>
        <div className="text-center">
          <div className="label-eyebrow">Your seat is reserved</div>
          <h3 className="mt-3 font-serif text-3xl text-bdu-cream leading-tight">
            Welcome to the <span className="text-bdu-gold italic">rally roll</span>, {(signup?.name || "").split(" ")[0] || "friend"}.
          </h3>
          <p className="mt-3 text-sm text-bdu-cream/60 leading-relaxed">
            You're <span className="text-bdu-gold font-semibold">#{position}</span> of {total} on the launch list.
            The second we hit {goal} RSVPs, we lock the date, venue, and livestream link — and you'll be the first to know.
          </p>

          <div className="mt-5">
            <div className="h-2 bg-bdu-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-bdu-gold to-amber-300"
                style={{
                  width: `${Math.min(100, progress_pct)}%`,
                  transition: "width 900ms cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[0.65rem] uppercase tracking-widest text-bdu-cream/50">
              <span>{total} reserved</span>
              <span>Target · {goal}</span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={onShare}
              className="btn-gold text-sm"
              data-testid="rally-confirmation-share-btn"
            >
              <Share2 size={14} /> Pull 3 friends with you
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost text-sm"
              data-testid="rally-confirmation-close-btn"
            >
              Done
            </button>
          </div>
          <p className="mt-5 text-[0.6rem] uppercase tracking-widest text-bdu-cream/35 leading-relaxed">
            A confirmation email is on the way. Add{" "}
            <span className="text-bdu-cream/55">greg@blackdollarsunited.org</span> to your contacts.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Rally() {
  const [stats, setStats] = useState({ total: 0, goal: 500, progress_pct: 0, recent: [], by_state: [] });
  const [confirmation, setConfirmation] = useState(null);

  const loadStats = () => {
    api
      .get("/rally/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    loadStats();
    const t = setInterval(loadStats, 25000);
    return () => clearInterval(t);
  }, []);

  const share = async () => {
    const url = window.location.origin + "/rally";
    const text =
      "RALLY OF THE MINDS · THE RISE II POWER MOVEMENT. Black Dollars United is launching the proposed Trillion Dollar Spending Power Federal Credit Union. Reserve your seat 👇";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Rally Of The Minds", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success("Link copied — share it with your people.");
      }
    } catch (_) {}
  };

  const progressPct = Math.min(100, stats.progress_pct || 0);

  return (
    <div className="grain bg-bdu-bg text-bdu-cream min-h-screen" data-testid="rally-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-bdu-gold/15"
        data-testid="rally-hero-section"
      >
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: "url('/brand/rally-of-the-minds-flyer.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(28px) saturate(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/85 to-bdu-bg" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(1000px 500px at 50% 10%, rgba(212,175,55,0.18) 0%, rgba(0,0,0,0) 65%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-14 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div
              className="inline-flex items-center gap-2 label-eyebrow"
              style={{ color: "rgba(212,175,55,0.9)" }}
              data-testid="rally-eyebrow"
            >
              <Megaphone size={14} /> Launch Rally · Date To Be Announced
            </div>

            <h1
              className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
              data-testid="rally-headline"
            >
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, #FFFFFF 0%, #E8E8E8 20%, #D4AF37 55%, #B0902B 100%)",
                  filter: "drop-shadow(0 2px 20px rgba(212,175,55,0.25))",
                }}
              >
                Rally Of The Minds
              </span>
              <span className="block mt-3 text-bdu-cream text-3xl sm:text-4xl lg:text-5xl">
                The <span className="italic text-bdu-gold">Rise II Power</span> Movement
              </span>
            </h1>

            <p className="mt-6 text-lg text-bdu-cream/75 leading-relaxed max-w-xl">
              Black Dollars United is officially launching the proposed Trillion Dollar Spending Power Federal Credit Union.
              Like-minded minds must unite, strategize, and use brain power to protect our own economy.
              This is our blueprint for generational wealth and community leadership.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-4 max-w-lg" data-testid="rally-stats-row">
              <div className="bg-black/60 backdrop-blur-sm border border-bdu-gold/25 p-4">
                <div className="text-[0.55rem] uppercase tracking-widest text-bdu-cream/50">
                  Rally Date
                </div>
                <div className="mt-1 font-serif text-xl text-bdu-cream flex items-center gap-2">
                  <CalendarClock size={16} className="text-bdu-gold" /> TBA
                </div>
              </div>
              <div className="bg-black/60 backdrop-blur-sm border border-bdu-gold/25 p-4">
                <div className="text-[0.55rem] uppercase tracking-widest text-bdu-cream/50">
                  Venue
                </div>
                <div className="mt-1 font-serif text-xl text-bdu-cream flex items-center gap-2">
                  <MapPin size={16} className="text-bdu-gold" /> Atlanta, GA
                </div>
              </div>
              <div className="bg-black/60 backdrop-blur-sm border border-bdu-gold/25 p-4">
                <div className="text-[0.55rem] uppercase tracking-widest text-bdu-cream/50">
                  RSVP'd
                </div>
                <div
                  className="mt-1 font-serif text-xl text-bdu-cream flex items-center gap-2"
                  data-testid="rally-stat-total"
                >
                  <Users size={16} className="text-bdu-gold" />
                  {stats.total}
                  <span className="text-xs text-bdu-cream/40"> / {stats.goal}</span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="h-2 bg-bdu-cream/10 rounded-full overflow-hidden max-w-lg">
                <div
                  className="h-full bg-gradient-to-r from-bdu-gold to-amber-300"
                  style={{
                    width: `${progressPct}%`,
                    transition: "width 900ms cubic-bezier(0.4,0,0.2,1)",
                  }}
                />
              </div>
              <div className="mt-1.5 text-[0.65rem] uppercase tracking-widest text-bdu-cream/40 max-w-lg flex items-center justify-between">
                <span>{stats.total} supporters reserved</span>
                <span>
                  {Math.max(0, (stats.goal || 0) - (stats.total || 0))} to unlock date
                </span>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#rsvp"
                className="btn-gold"
                data-testid="rally-hero-primary-cta"
              >
                Reserve My Seat <ArrowRight size={16} />
              </a>
              <button
                type="button"
                onClick={share}
                className="btn-ghost"
                data-testid="rally-hero-share-cta"
              >
                <Share2 size={14} /> Share The Rally
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <HeroVideo />
          </div>
        </div>
      </section>

      {/* LIVE TICKER */}
      <LiveTicker recent={stats.recent} />

      {/* FLYER + MANIFESTO */}
      <section className="relative border-b border-bdu-gold/10" data-testid="rally-manifesto-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="label-eyebrow">The Manifesto</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
              A new <span className="italic text-bdu-gold">political strategy</span> for economic empowerment.
            </h2>
            <p className="mt-6 text-base text-bdu-cream/75 leading-relaxed">
              We cannot rely on an administration turning its back on civil rights, DEI programs,
              and displacing 600,000 Black women. This is our blueprint for generational wealth and community leadership —
              built, financed, and owned by us.
            </p>
            <blockquote className="mt-8 pl-6 border-l-2 border-bdu-gold">
              <div className="font-serif italic text-xl sm:text-2xl text-bdu-cream leading-snug">
                "The only way our community can rise to power is by practicing{" "}
                <span className="text-bdu-gold">group economic empowerment.</span>"
              </div>
              <div className="mt-3 text-xs uppercase tracking-widest text-bdu-cream/50">
                — Greg L Bradford · Founder, CEO, President
              </div>
            </blockquote>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <div className="bg-bdu-surface2 border border-bdu-gold/20 p-5">
                <div className="label-eyebrow text-bdu-gold">Mission</div>
                <div className="mt-2 font-serif text-2xl text-bdu-cream">
                  $50M · 24 months
                </div>
                <p className="mt-2 text-xs text-bdu-cream/60">
                  Build hospitals, medical facilities, schools, food franchises, manufacturing, and banks.
                </p>
              </div>
              <div className="bg-bdu-surface2 border border-bdu-gold/20 p-5">
                <div className="label-eyebrow text-bdu-gold">Phase 2 · Now Active</div>
                <div className="mt-2 font-serif text-2xl text-bdu-cream">
                  NCUA charter approval
                </div>
                <p className="mt-2 text-xs text-bdu-cream/60">
                  3,000 pledge signatures · $500,000 seed capital in escrow at Citizens Trust Bank.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <a
              href="/brand/rally-of-the-minds-flyer.jpg"
              target="_blank"
              rel="noreferrer"
              className="block relative group"
              data-testid="rally-flyer-link"
            >
              <div
                className="absolute -inset-[2px]"
                style={{
                  background:
                    "linear-gradient(135deg, #D4AF37 0%, #4A3A10 30%, #F5D97A 50%, #8A6D1E 70%, #D4AF37 100%)",
                  filter: "drop-shadow(0 30px 60px rgba(212,175,55,0.3))",
                }}
              />
              <div className="relative overflow-hidden">
                <img
                  src="/brand/rally-of-the-minds-flyer.jpg"
                  alt="Rally Of The Minds · The Rise II Power Movement — official flyer"
                  className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                  data-testid="rally-flyer-img"
                />
              </div>
              <div className="mt-3 text-center text-[0.65rem] uppercase tracking-widest text-bdu-cream/40">
                Tap to download the official flyer
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* RSVP SECTION */}
      <section
        id="rsvp"
        className="relative border-b border-bdu-gold/10"
        data-testid="rally-rsvp-section"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
          <RSVPForm onSuccess={(res) => { setConfirmation(res); loadStats(); }} />

          {stats.by_state && stats.by_state.length > 0 && (
            <div className="mt-14" data-testid="rally-by-state">
              <div className="label-eyebrow text-center">The movement is already everywhere</div>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {stats.by_state.map((row) => (
                  <div
                    key={row.state}
                    className="bg-bdu-surface2 border border-bdu-gold/20 px-4 py-2.5 flex items-center gap-2"
                  >
                    <span className="font-serif text-lg text-bdu-gold">{row.state}</span>
                    <span className="text-xs text-bdu-cream/55">{row.count} RSVPs</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ADMINISTERED BY */}
      <section className="bg-black/60 border-t border-bdu-gold/10 py-10" data-testid="rally-footer-disclosure">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 text-center">
          <div className="label-eyebrow text-bdu-cream/50">Administered By</div>
          <div className="mt-3 font-serif text-xl text-bdu-cream">Citizens Trust Bank — A BXD Historic Bank</div>
          <p className="mt-4 text-[0.7rem] uppercase tracking-widest text-bdu-cream/40 max-w-3xl mx-auto leading-relaxed">
            The Rally Of The Minds supports the proposed Trillion Dollar Spending Power Federal Credit Union,
            pending NCUA charter approval. Charter-support pledges are non-binding, held in escrow at Citizens Trust Bank,
            and refunded in full if the charter is not granted.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-bdu-cream/60">
            <a href="mailto:greg@blackdollarsunited.org" className="hover:text-bdu-gold">
              greg@blackdollarsunited.org
            </a>
            <span className="text-bdu-cream/20">·</span>
            <a href="tel:+14044070434" className="hover:text-bdu-gold">
              404-407-0434 (text only)
            </a>
          </div>
        </div>
      </section>

      <ConfirmationCard
        result={confirmation}
        onShare={share}
        onClose={() => setConfirmation(null)}
      />
    </div>
  );
}
